import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authService } from '@/services/authService'
import { Eye, EyeOff, Check, X, Lock } from 'lucide-react'
import { cn } from '@/utils/cn'

function getPasswordStrength(password: string) {
  const checks = {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  }
  const passed = Object.values(checks).filter(Boolean).length
  let level: 'none' | 'weak' | 'medium' | 'strong' = 'none'
  if (password.length === 0) level = 'none'
  else if (passed <= 2) level = 'weak'
  else if (passed <= 4) level = 'medium'
  else level = 'strong'
  return { checks, passed, level }
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const passwordStrength = getPasswordStrength(password)

  useEffect(() => {
    if (!token) {
      setError('Link de recuperação inválido. Solicite um novo.')
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!token) {
      setError('Link de recuperação inválido')
      return
    }

    if (passwordStrength.level !== 'strong') {
      setError('A senha não atende todos os requisitos de segurança')
      return
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem')
      return
    }

    setLoading(true)

    try {
      await authService.resetPassword(token, password)
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao redefinir senha. O link pode ter expirado.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-verde-50 to-verde-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Senha Redefinida!
            </h1>
            <p className="text-gray-600 mb-6">
              Sua senha foi redefinida com sucesso. Agora você pode fazer login com sua nova senha.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-verde-500 text-white py-3 rounded-lg font-medium hover:bg-verde-600 transition-colors"
            >
              Ir para Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-verde-50 to-verde-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-verde-100 rounded-full mb-4">
            <Lock className="h-8 w-8 text-verde-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Redefinir Senha
          </h1>
          <p className="text-gray-600">
            Digite sua nova senha abaixo.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Nova Senha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nova Senha
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cn(
                  "w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:ring-verde-500 focus:border-transparent text-gray-900",
                  passwordStrength.level === 'weak' && "border-red-500",
                  passwordStrength.level === 'medium' && "border-yellow-500",
                  passwordStrength.level === 'strong' && "border-green-500"
                )}
                placeholder="Digite sua nova senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all duration-300",
                        passwordStrength.level === 'weak' && "w-1/3 bg-red-500",
                        passwordStrength.level === 'medium' && "w-2/3 bg-yellow-500",
                        passwordStrength.level === 'strong' && "w-full bg-green-500"
                      )}
                    />
                  </div>
                  <span className={cn(
                    "text-xs font-medium",
                    passwordStrength.level === 'weak' && "text-red-500",
                    passwordStrength.level === 'medium' && "text-yellow-500",
                    passwordStrength.level === 'strong' && "text-green-500"
                  )}>
                    {passwordStrength.level === 'weak' && 'Fraca'}
                    {passwordStrength.level === 'medium' && 'Média'}
                    {passwordStrength.level === 'strong' && 'Forte'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={cn("flex items-center gap-1", passwordStrength.checks.minLength && "text-green-500")}>
                    {passwordStrength.checks.minLength ? <Check size={12} /> : <X size={12} />}
                    Mínimo 8 caracteres
                  </div>
                  <div className={cn("flex items-center gap-1", passwordStrength.checks.hasUpper && "text-green-500")}>
                    {passwordStrength.checks.hasUpper ? <Check size={12} /> : <X size={12} />}
                    Letra maiúscula
                  </div>
                  <div className={cn("flex items-center gap-1", passwordStrength.checks.hasLower && "text-green-500")}>
                    {passwordStrength.checks.hasLower ? <Check size={12} /> : <X size={12} />}
                    Letra minúscula
                  </div>
                  <div className={cn("flex items-center gap-1", passwordStrength.checks.hasNumber && "text-green-500")}>
                    {passwordStrength.checks.hasNumber ? <Check size={12} /> : <X size={12} />}
                    Número
                  </div>
                  <div className={cn("flex items-center gap-1", passwordStrength.checks.hasSpecial && "text-green-500")}>
                    {passwordStrength.checks.hasSpecial ? <Check size={12} /> : <X size={12} />}
                    Caractere especial
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirmar Senha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={cn(
                  "w-full px-4 py-3 pr-10 border rounded-lg focus:ring-2 focus:ring-verde-500 focus:border-transparent text-gray-900",
                  confirmPassword && confirmPassword !== password && "border-red-500"
                )}
                placeholder="Confirme sua nova senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {confirmPassword && confirmPassword !== password && (
              <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || passwordStrength.level !== 'strong' || password !== confirmPassword}
            className="w-full bg-verde-500 text-white py-3 rounded-lg font-medium hover:bg-verde-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Voltar para Login
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
